import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import CharacterCount from '@tiptap/extension-character-count'
import Document from '@tiptap/extension-document'
import Heading from '@tiptap/extension-heading'
import History from '@tiptap/extension-history'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import Mention from '@tiptap/extension-mention'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Strike from '@tiptap/extension-strike'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import {
  Editor,
  markInputRule,
  markPasteRule,
  mergeAttributes,
  useEditor
} from '@tiptap/react'
import { useMemo } from 'react'

import { MentionedType } from '@/shared/types/post/enums/MentionedType'
import { usePersonaBaseUrl } from '@/ui/hooks/usePersonaBaseUrl'

import { MentionModel } from '../Mention/MentionModel'
import { MentionStorage } from '../Mention/MentionStorage'
import MentionSuggestionRender from '../Mention/MentionSuggestionRender'
import { getMarkdownMentionStorage } from '../utils/getMarkdownMentionStorage'
import { markdownSerializer } from './markdownSerializer'

const parseMarkRegex = {
  star: /(?!^\s)(\*(?!\s+\*)((?:[^*]+))\*(?!\s+\*))/g,
  underscore: /(?:^|\s)(_(?!\s+_)((?:[^_]+))_(?!\s+_))/g,
  strike: /(?:^|\s)(~(?!\s+~)((?:[^~]+))~(?!\s+~))/g,
  ins: /(?:^|\s)(<\/?ins>(?!\s+<\/?ins>)((?:[^<]+))<\/ins>(?!\s+<\/?ins>))/g
}

export default function useMarkdownEditor({
  limit,
  onChange,
  mentionQuery,
  isReadonly,
  mentionKey
}: {
  limit?: number
  onChange?: (value?: string) => void
  mentionQuery?: (query: string) => void
  isReadonly?: boolean
  mentionKey?: string
} = {}) {
  const personaBaseUrl = usePersonaBaseUrl()

  const component = useMemo(
    () => MentionSuggestionRender({ mentionKey: mentionKey ?? '' }),
    [mentionKey]
  )

  const editor = useEditor(
    {
      extensions: [
        Document,
        History,
        Placeholder.configure({
          placeholder: 'Digite aqui sua mensagem',
          emptyEditorClass: 'is-editor-empty'
        }),
        CharacterCount.configure({
          limit: limit
        }),
        Italic.extend({
          addInputRules() {
            return [
              markInputRule({
                find: parseMarkRegex.underscore,
                type: this.type
              })
            ]
          },
          addPasteRules() {
            return [
              markPasteRule({
                find: parseMarkRegex.underscore,
                type: this.type
              })
            ]
          }
        }),
        Bold.extend({
          addInputRules() {
            return [
              markInputRule({
                find: parseMarkRegex.star,
                type: this.type
              })
            ]
          },
          addPasteRules() {
            return [
              markPasteRule({
                find: parseMarkRegex.star,
                type: this.type
              })
            ]
          }
        }),
        Strike.extend({
          addInputRules() {
            return [
              markInputRule({
                find: parseMarkRegex.strike,
                type: this.type
              })
            ]
          },
          addPasteRules() {
            return [
              markPasteRule({
                find: parseMarkRegex.strike,
                type: this.type
              })
            ]
          }
        }),
        Underline.extend({
          addInputRules() {
            return [
              markInputRule({
                find: parseMarkRegex.ins,
                type: this.type
              })
            ]
          },
          addPasteRules() {
            return [
              markPasteRule({
                find: parseMarkRegex.ins,
                type: this.type
              })
            ]
          },
          parseHTML() {
            return [
              {
                tag: 'ins'
              }
            ]
          }
        }),
        Link.extend({
          exitable: true
        }).configure({
          openOnClick: false
        }),
        Text,
        Heading.configure({
          levels: [1, 2, 3]
        }),
        Paragraph,
        BulletList,
        ListItem,
        OrderedList,
        MentionStorage,
        Mention.extend({
          addAttributes() {
            return {
              id: {
                default: null,
                parseHTML: (element) =>
                  `${element.getAttribute(
                    'href'
                  )}/${element.textContent?.replace(
                    this.options.suggestion.char?.toString() ?? '@',
                    ''
                  )}`,
                renderHTML: (attributes) => {
                  if (!attributes.id) {
                    return {}
                  }
                  return {
                    'data-id': attributes.id
                  }
                }
              }
            }
          },
          renderHTML({ node, HTMLAttributes }) {
            const parts = (
              (node.attrs?.id as MentionModel | undefined) ?? ''
            ).split('/')

            /// O contrato de menção antigo tinha apenas duas propriedades. Entretanto, para que a menção
            /// de grupo fosse adicionada, foi necessário adicionar o tipo de menção ao contrato já existente
            /// em produção. Por isso, o código abaixo é necessário para que as postagens antigas não quebrem.
            const id = parts.length === 3 ? parts[1] : parts[0]
            const mentionedType =
              parts.length === 3 ? parts[0] : MentionedType.enum.PERSON
            const avatar = parts[parts.length - 1]

            if (mentionedType === 'ACCESS_PROFILE') {
              return [
                'span',
                {
                  ...HTMLAttributes,
                  role: 'button',
                  class: 'mention-access-profile',
                  'data-id': id,
                  'data-avatar': avatar,
                  'data-type': 'access-profile',
                  style: 'color: #007bff; cursor: pointer;'
                },
                `@${avatar}`
              ]
            }

            const href =
              mentionedType === MentionedType.enum.GROUP
                ? `${personaBaseUrl}/groups?filter=ALL&groupId=${id}`
                : `${personaBaseUrl}/profile-info/${id}`

            return [
              'a',
              mergeAttributes(HTMLAttributes, {
                href: href,
                target: '_blank',
                rel: 'noopener noreferrer',
                'data-type': 'mention'
              }),
              `${this.options.suggestion.char ?? '@'}${avatar}`
            ]
          },
          parseHTML: () => {
            return [
              {
                tag: 'a',
                getAttrs: (node) => {
                  const id = (node.getAttribute('href') ?? '').includes('/')
                    ? node.getAttribute('href')?.split('/')[1]
                    : node.getAttribute('href')

                  const text = node.textContent?.replace('@', '') ?? ''
                  const uuidRegex = /^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i
                  const numberRegex = /^\d+$/

                  return (id && id.match(uuidRegex)) ||
                    (id && id.match(numberRegex))
                    ? { 'data-href-id': `${id}/${text}` }
                    : false
                },
                priority: 1500
              }
            ]
          }
        }).configure({
          renderHTML({ options, node }) {
            const [mentionedType, id, avatar] = (
              (node.attrs?.id as MentionModel | undefined) ?? ''
            ).split('/')

            const href =
              mentionedType === MentionedType.enum.GROUP
                ? `${personaBaseUrl}/groups/${id}`
                : `${personaBaseUrl}/profile-info/${id}`

            return [
              mentionedType === MentionedType.enum.ACCESS_PROFILE
                ? 'span'
                : 'a',
              mergeAttributes(options, {
                href: href,
                target: '_blank',
                rel: 'noopener noreferrer',
                'data-type':
                  mentionedType === MentionedType.enum.ACCESS_PROFILE
                    ? 'access-profile'
                    : 'mention'
              }),
              `${options.suggestion.char}${avatar}`
            ]
          },
          suggestion: {
            items: ({ query, editor }) => {
              mentionQuery?.(query)
              return getMarkdownMentionStorage(
                editor as Editor,
                mentionKey ?? '0'
              ).suggestions
            },
            render: component
          }
        })
      ],
      editable: !isReadonly,
      onUpdate: ({ editor }) => {
        const content = markdownSerializer(editor.schema, editor.getJSON())
        onChange?.(content)
      }
    },
    [component]
  )

  return editor
}
